export function initFabric({ canvas, hero, onReady, onRevealed, onUnavailable }) {
  if (!canvas?.transferControlToOffscreen || typeof Worker === "undefined") {
    onUnavailable();
    return;
  }

  const listeners = new AbortController();
  const touch = matchMedia("(hover: none) and (pointer: coarse)");
  const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  const mobilePreview = new URLSearchParams(location.search).has("mobile-reveal");
  let worker;
  let stopped = false;
  let ready = false;
  let revealed = false;
  let scrollFrame = 0;
  let pointerSequence = 0;
  let pointerInFlight = 0;
  let pointerQueued = false;
  let resizeFrame = 0;
  let releaseTimer = 0;
  let deadline = 0;
  let pointer;
  let size;
  let geometryKey = '';
  let lastScrollPosition = -1;
  let lastScrollHidden;

  function measure() {
    const rect = canvas.getBoundingClientRect();
    size = { width: rect.width, height: rect.height, heroHeight: hero.offsetHeight };
    return { ...size, dpr: devicePixelRatio || 1,
      scrollY: scrollY || 0, hidden: document.hidden, touch: touch.matches,
      reducedMotion: reducedMotion.matches, mobilePreview };
  }

  function geometry(view) {
    return [view.width, view.height, view.heroHeight, view.dpr,
      view.touch, view.reducedMotion, view.mobilePreview].join(':');
  }

  function stop() {
    stopped = true;
    listeners.abort();
    cancelAnimationFrame(scrollFrame);
    pointerInFlight = 0;
    pointerQueued = false;
    cancelAnimationFrame(resizeFrame);
    clearTimeout(releaseTimer);
    clearTimeout(deadline);
    worker?.terminate();
  }

  function unavailable() {
    if (stopped) return;
    stop();
    onUnavailable();
  }

  function send(type, payload = {}) {
    if (stopped) return;
    try { worker.postMessage({ type, ...payload }); }
    catch { unavailable(); }
  }

  try {
    worker = new Worker(new URL("./fabric.worker.js", import.meta.url), { type: "module" });
    worker.onerror = unavailable;
    worker.onmessage = ({ data }) => {
      if (stopped) return;
      if (data.type === "unavailable") return unavailable();
      if (data.type === "pointer-rendered" && data.sequence === pointerInFlight) {
        pointerInFlight = 0;
        sendPointer();
      }
      if (data.type === "ready" && !ready) {
        ready = true;
        clearTimeout(deadline);
        Promise.resolve(onReady()).then(() => send("reveal")).catch(unavailable);
      }
      if (data.type === "revealed" && ready && !revealed) {
        revealed = true;
        onRevealed?.();
      }
    };
    const view = measure();
    geometryKey = geometry(view);
    const surface = canvas.transferControlToOffscreen();
    worker.postMessage({ type: "init", canvas: surface, view }, [surface]);
    deadline = setTimeout(unavailable, 15000);
  } catch {
    unavailable();
    return;
  }

  const options = { passive: true, signal: listeners.signal };
  function syncScroll(type = "scroll") {
    const depth = mobilePreview && (size.width < 720 || touch.matches)
      ? size.heroHeight * 0.58 : size.height * 1.04 * 0.27;
    const position = Math.min(Math.max(depth, 1), Math.max(0, scrollY));
    // The settled fabric remains as the background. Scrolling through projects
    // must not wake the full-screen shader after its scroll state has saturated.
    if (type === "scroll" && position === lastScrollPosition && document.hidden === lastScrollHidden) return;
    lastScrollPosition = position;
    lastScrollHidden = document.hidden;
    send(type, { view: { scrollY, hidden: document.hidden } });
  }
  window.addEventListener("scroll", () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      syncScroll();
    });
  }, options);
  window.addEventListener("projectanchorrestore", () => syncScroll("anchor"), options);

  function queueResize() {
    if (!resizeFrame) resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      const view = measure();
      const nextGeometry = geometry(view);
      if (nextGeometry !== geometryKey) {
        geometryKey = nextGeometry;
        lastScrollPosition = -1;
        send("resize", { view });
      }
    });
  }
  window.addEventListener("resize", queueResize, options);
  touch.addEventListener("change", queueResize, options);
  reducedMotion.addEventListener("change", queueResize, options);

  function sendPointer(force = false) {
    if (!pointerQueued || (pointerInFlight && !force)) return;
    pointerQueued = false;
    pointerInFlight = ++pointerSequence;
    send("pointer", { pointer, sequence: pointerInFlight });
  }
  function move(event) {
    if (document.hidden || reducedMotion.matches || scrollY >= innerHeight * 0.6) return;
    clearTimeout(releaseTimer);
    pointer = { x: event.clientX / size.width, y: 1 - event.clientY / size.height, active: true };
    pointerQueued = true;
    // Deliver the first sample immediately. While the worker draws it, retain
    // only the newest sample instead of adding a main-thread frame delay.
    sendPointer();
  }
  function release() {
    if (pointer?.active) {
      pointer = { ...pointer, active: false };
      pointerQueued = true;
      // A release takes priority over queued motion. Sequence IDs make any
      // acknowledgement for the superseded packet harmless.
      sendPointer(true);
    }
  }
  window.addEventListener("pointermove", move, options);
  window.addEventListener("pointerdown", move, options);
  for (const name of ["pointerup", "pointercancel"]) {
    window.addEventListener(name, (event) => {
      if (event.pointerType === "touch" || event.pointerType === "pen") {
        clearTimeout(releaseTimer);
        releaseTimer = setTimeout(release, 620);
      }
    }, options);
  }
  window.addEventListener("pointerleave", release, options);
  window.addEventListener("blur", release, options);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) release();
    send("visibility", { view: { hidden: document.hidden, scrollY } });
  }, options);
  window.addEventListener("pageshow", () => {
    queueResize();
    syncScroll("visibility");
  }, options);
  window.addEventListener("pagehide", (event) => {
    if (event.persisted) send("visibility", { view: { hidden: true } });
    else stop();
  }, options);
  syncScroll();
  return stop;
}
